/**
 * Precision Responsible AI, the life of an approved tool (COM028 step two).
 *
 *   OwnerHome            My AI tools: what the Business Owner holds and what
 *                        each tool wants from them next (Sec 3, 9, 11)
 *   MonitoringPill       where a tool stands on monitoring, one glance
 *   MonitoringTab        the owner's monitoring reports and the form that
 *                        files one; compliance records its review (Sec 9)
 *   MaterialChangeModal  a change to the tool, reported before it ships;
 *                        the review reopens (Sec 6, 9)
 *   RetirementCard       the retirement lines in the policy's words; only
 *                        compliance retires the record (Sec 11)
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const INPUT = 'w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';
const LABEL = 'text-xs font-semibold text-slate-500';
const BTN = 'px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50';
// A date only value (a due date) is a calendar day, not a UTC midnight that
// would read as the day before in the Americas.
const fmtDate = (d) => { try { return d ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? `${d}T12:00:00` : d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; } catch { return '—'; } };
const fmtWhen = (d) => { try { return d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'; } catch { return '—'; } };

async function send(url, method, body) {
  const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const d = await r.json().catch(() => ({ success: false, message: 'Server error' }));
  if (!d.success) throw new Error(d.message || 'Request failed');
  return d;
}

export const MONITORING_META = {
  scheduled: { label: 'Monitoring on schedule', cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  due:       { label: 'Monitoring due',         cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  overdue:   { label: 'Monitoring past due',    cls: 'bg-red-50 text-red-700 ring-red-200' },
  unset:     { label: 'Monitoring months not set', cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
};

/** One glance at monitoring. Nothing is shown for a tool that is not approved. */
export function MonitoringPill({ m, verbose = false }) {
  if (!m || !MONITORING_META[m.state]) return null;
  const meta = MONITORING_META[m.state];
  const detail = m.state === 'scheduled' ? `next ${fmtDate(m.due_on)}` : m.state === 'due' ? `was due ${fmtDate(m.due_on)}` : m.state === 'overdue' ? `${m.days_past} days past ${fmtDate(m.due_on)}` : 'set in Program settings';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${meta.cls}`}>
      {meta.label}{verbose ? <span className="font-normal opacity-80">· {detail}</span> : null}
    </span>
  );
}

/** What the tool wants from its owner next, in one line. */
function nextStep(uc) {
  const m = uc.monitoring;
  if (uc.status === 'retired') return null;
  if (uc.retirement_requested_at) return { text: 'Retirement in progress: work the checklist with compliance.', tone: 'text-slate-600' };
  if (uc.material_change_at && uc.status === 'in_review') return { text: 'Re-review in progress after the change you reported.', tone: 'text-amber-700' };
  if (m?.state === 'overdue') return { text: `Submit the monitoring report, ${m.days_past} days past due.`, tone: 'text-red-700' };
  if (m?.state === 'due') return { text: 'Submit the monitoring report.', tone: 'text-amber-700' };
  if (m?.state === 'scheduled') return { text: `Nothing owed until ${fmtDate(m.due_on)}. Report an issue or a change any time.`, tone: 'text-slate-500' };
  if (['approved', 'approved_with_contingencies'].includes(uc.status)) return { text: 'Nothing owed yet. Report an issue or a change any time.', tone: 'text-slate-500' };
  if (['proposed', 'in_review'].includes(uc.status)) return { text: 'With the compliance department and the committee.', tone: 'text-slate-500' };
  return null;
}

// ---------------------------------------------------------------------------
export function OwnerHome({ data, onOpen, onPropose, renderStatus }) {
  const owned = data.use_cases.filter((uc) => uc.mine);
  const proposals = data.use_cases.filter((uc) => !uc.mine);
  const Row = ({ uc, sub }) => (
    <button onClick={() => onOpen(uc.id)} className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900 truncate">{uc.name}</span>
          {renderStatus(uc)}
          <MonitoringPill m={uc.monitoring} />
        </div>
        {sub}
      </div>
      <span className="text-slate-300">›</span>
    </button>
  );
  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="text-[11px] font-bold tracking-[0.14em] text-orange-600 uppercase mb-1">Precision Responsible AI</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{owned.length ? 'My AI tools' : 'Thinking about AI in your department?'}</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            {owned.length
              ? 'The AI tools you are accountable for as Business Owner. Each one tells you what it needs from you next; everything is answered from the page of the tool.'
              : 'It starts here, before anything is built or bought. Propose the idea, answer six honest questions, and the governance committee takes it from there. You will always see where it stands.'}
          </p>
        </div>
        <button onClick={onPropose} className="px-5 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition-colors shadow-sm shrink-0">
          Propose an AI use case
        </button>
      </div>

      {owned.length > 0 && (
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {owned.map((uc) => {
            const n = nextStep(uc);
            return <Row key={uc.id} uc={uc} sub={n ? <div className={`text-xs mt-1 ${n.tone}`}>{n.text}</div> : null} />;
          })}
        </div>
      )}

      <div className="mt-10">
        <h2 className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-3">My proposals</h2>
        {proposals.length === 0 ? (
          <p className="text-sm text-slate-400">Nothing proposed yet.</p>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {proposals.map((uc) => <Row key={uc.id} uc={uc} sub={<div className="text-xs text-slate-500 mt-0.5">Proposed {fmtDate(uc.created_at)}</div>} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const SECTIONS = [
  ['performance_notes', 'Performance against the metrics', 'Any decline in performance since the last report, in words. The table above carries the numbers.'],
  ['errors', 'Errors and Critical Errors', 'Errors seen in the period. A Critical Error is an output that caused, or could have caused, harm to a member.'],
  ['drift', 'Model drift', 'A decline in performance over time from changes in data or operating conditions, or none observed.'],
  ['bias_fairness', 'Bias and fairness', 'Results compared across member groups (age, sex, language, race, ethnicity where available) against the baseline.'],
  ['complaints_incidents', 'Complaints and security incidents', 'Member or provider complaints and security incidents involving the tool, or none.'],
  ['changes', 'Changes to the tool', 'Anything that changed in the period: process, scope, vendor, model, risk level, a screening answer.'],
];

export function MonitoringTab({ d, ucId, canWork, compliance, onChanged }) {
  const uc = d.use_case;
  const m = d.monitoring;
  const reports = d.monitoring_reports || [];
  const monitored = ['approved', 'approved_with_contingencies'].includes(uc.status);
  const [filing, setFiling] = useState(false);
  const latestTest = (d.test_records || [])[0];
  const carried = (latestTest?.metrics || []).map((x) => ({ metric: x.metric, target: x.target, result: '', met: false }));
  const [f, setF] = useState({ period_label: '', metrics: carried.length ? carried : [{ metric: '', target: '', result: '', met: false }], performance_notes: '', errors: '', critical_errors: 0, drift: '', bias_fairness: '', complaints_incidents: '', changes: '', material_change: false, attested: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const setMetric = (i, k, v) => setF({ ...f, metrics: f.metrics.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await send(`${API}/api/ai-command/use-cases/${ucId}/monitoring`, 'POST', { ...f, critical_errors: parseInt(f.critical_errors, 10) || 0 });
      setFiling(false); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">
          The Business Owner monitors the tool and reports the results to compliance at least as often as its risk rating requires. Compliance reviews each report and prepares the analysis for the committee.
        </p>
        <MonitoringPill m={m} verbose />
      </div>
      {m?.state === 'unset' && monitored && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          {compliance ? 'Monitoring months per risk rating are not set yet. Set them under Committee and champions, Program settings; until then nothing is owed and no owner is reminded.' : 'Compliance has not set the monitoring frequency yet. Nothing is owed until it is; you may still file a report.'}
        </div>
      )}
      {!monitored && <p className="text-sm text-slate-400">Monitoring reports are filed once the tool is approved.</p>}

      {reports.length > 0 && (
        <ul className="space-y-3">{reports.map((r) => <ReportCard key={r.id} r={r} ucId={ucId} compliance={compliance} onChanged={onChanged} />)}</ul>
      )}
      {monitored && reports.length === 0 && !filing && <p className="text-sm text-slate-400">No monitoring report filed yet.</p>}

      {canWork && monitored && !filing && (
        <button onClick={() => setFiling(true)} className={`${BTN} bg-orange-600 text-white hover:bg-orange-700`}>Submit a monitoring report</button>
      )}
      {filing && (
        <div className="rounded-xl border border-slate-200 p-4 space-y-4 bg-white">
          <div>
            <div className="text-sm font-semibold text-slate-800">Monitoring report</div>
            <div className="text-xs text-slate-500 mt-0.5">A few minutes. The metrics come from the pre implementation test; enter the results and answer each section in a line or two, or say none.</div>
          </div>
          <div><label className={LABEL}>Period covered</label><input className={INPUT} placeholder="e.g. July to September 2026" value={f.period_label} onChange={(e) => setF({ ...f, period_label: e.target.value })} /></div>
          <div>
            <label className={LABEL}>Performance against the metrics</label>
            <div className="space-y-2 mt-1">
              {f.metrics.map((x, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className={`${INPUT} col-span-4`} placeholder="Metric" value={x.metric} onChange={(e) => setMetric(i, 'metric', e.target.value)} />
                  <input className={`${INPUT} col-span-3`} placeholder="Target" value={x.target} onChange={(e) => setMetric(i, 'target', e.target.value)} />
                  <input className={`${INPUT} col-span-3`} placeholder="Result this period" value={x.result} onChange={(e) => setMetric(i, 'result', e.target.value)} />
                  <label className="col-span-2 flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={x.met} onChange={(e) => setMetric(i, 'met', e.target.checked)} /> Met</label>
                </div>
              ))}
              <button type="button" onClick={() => setF({ ...f, metrics: [...f.metrics, { metric: '', target: '', result: '', met: false }] })} className="text-xs font-semibold text-orange-700 hover:underline">Add a metric</button>
            </div>
          </div>
          {SECTIONS.map(([k, label, hint]) => (
            <div key={k}>
              <label className={LABEL}>{label}</label>
              <div className="text-[11px] text-slate-400 mb-1">{hint}</div>
              <textarea className={INPUT} rows={2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              {k === 'errors' && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                  <span>Critical Errors in the period</span>
                  <input type="number" min={0} className="w-20 px-2 py-1 rounded-lg border border-slate-200 text-sm" value={f.critical_errors} onChange={(e) => setF({ ...f, critical_errors: e.target.value })} />
                  {parseInt(f.critical_errors, 10) > 0 && <span className="text-xs text-red-700">Each one must also be logged as a critical issue on the Issues tab, within one business day of discovery.</span>}
                </div>
              )}
              {k === 'changes' && (
                <label className="mt-2 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={f.material_change} onChange={(e) => setF({ ...f, material_change: e.target.checked })} />
                  <span>This is a Material Change. {d.settings?.material_change_definition ? <span className="text-slate-500">Our definition: {d.settings.material_change_definition}</span> : <span className="text-slate-500">A change in the process, something added or removed, a change in risk level, a changed screening answer, or a change of vendor or model.</span>} Ticking this reopens the review before the change ships.</span>
                </label>
              )}
            </div>
          ))}
          <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <input type="checkbox" className="mt-0.5" checked={f.attested} onChange={(e) => setF({ ...f, attested: e.target.checked })} />
            <span>I attest that this report is accurate and complete to the best of my knowledge.</span>
          </label>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setFiling(false)} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>
            <button onClick={submit} disabled={busy || !f.attested} className={`${BTN} bg-slate-900 text-white`}>{busy ? 'Submitting…' : 'Submit the report'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard({ r, ucId, compliance, onChanged }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const review = async () => {
    setBusy(true);
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/monitoring/${r.id}`, 'PATCH', { review_note: note }); onChanged(); } finally { setBusy(false); }
  };
  const metrics = Array.isArray(r.metrics) ? r.metrics : [];
  return (
    <li className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === 'reviewed' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>{r.status === 'reviewed' ? 'REVIEWED' : 'AWAITING REVIEW'}</span>
        <span>submitted {fmtWhen(r.submitted_at)} by {r.submitted_by_label || 'the Business Owner'}{r.period_label ? ` · ${r.period_label}` : ''}</span>
        {r.critical_errors > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700">{r.critical_errors} CRITICAL ERROR{r.critical_errors === 1 ? '' : 'S'}</span>}
        {r.material_change && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">MATERIAL CHANGE</span>}
      </div>
      {metrics.length > 0 && (
        <table className="mt-2 w-full text-sm">
          <thead><tr className="text-[11px] text-slate-400 uppercase text-left"><th className="py-1 pr-3">Metric</th><th className="py-1 pr-3">Target</th><th className="py-1 pr-3">Result</th><th className="py-1">Met</th></tr></thead>
          <tbody>{metrics.map((x, i) => (
            <tr key={i} className="border-t border-slate-100"><td className="py-1 pr-3 text-slate-800">{x.metric}</td><td className="py-1 pr-3 text-slate-600">{x.target}</td><td className="py-1 pr-3 text-slate-600">{x.result}</td><td className={`py-1 font-bold ${x.met ? 'text-emerald-600' : 'text-rose-600'}`}>{x.met ? 'Yes' : 'No'}</td></tr>
          ))}</tbody>
        </table>
      )}
      <div className="mt-2 grid md:grid-cols-2 gap-x-4 gap-y-1.5">
        {SECTIONS.map(([k, label]) => r[k] ? <p key={k} className="text-xs text-slate-600"><b className="text-slate-700">{label}:</b> {r[k]}</p> : null)}
      </div>
      {r.status === 'reviewed' ? (
        <p className="text-xs text-emerald-800 mt-2"><b>Compliance review</b> {fmtWhen(r.reviewed_at)} by {r.reviewed_by_label || 'compliance'}{r.review_note ? `: ${r.review_note}` : ''}</p>
      ) : compliance ? (
        <div className="mt-2 flex gap-2">
          <input className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs" placeholder="Review note, for the committee analysis (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button onClick={review} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold disabled:opacity-50">Mark reviewed</button>
        </div>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
export function MaterialChangeModal({ d, ucId, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/material-change`, 'POST', { note }); onDone(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-auto py-16 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Report a change to {d.use_case.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Before it ships. The review reopens: the assessment is revised, the testing repeated, and the committee decides again.</p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-600">
            <b>A Material Change is</b> {d.settings?.material_change_definition || 'a change in the process the tool supports, something added to or removed from what it does, a change in its risk level, a changed screening answer, or a change of vendor or model.'}
          </div>
          <div><label className={LABEL}>What is changing *</label><textarea className={INPUT} rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changes, why, and when it would take effect" /></div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>
          <button onClick={submit} disabled={busy || !note.trim()} className={`${BTN} bg-slate-900 text-white`}>{busy ? 'Reporting…' : 'Report the change'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
export function RetirementCard({ d, ucId, compliance, canWork, onChanged }) {
  const uc = d.use_case;
  const lines = d.retirement?.lines || [];
  const complete = !!d.retirement?.complete;
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const retired = uc.status === 'retired';
  const started = !!uc.retirement_requested_at || retired;
  if (!canWork && !started) return null;
  if (['proposed', 'declined'].includes(uc.status) && !started) return null;

  const request = async () => {
    setBusy('request'); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/retirement/request`, 'POST', { reason }); setAsking(false); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const toggle = async (line, done) => {
    setBusy(line.key); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/retirement/${line.key}`, 'PATCH', { done }); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const retire = async () => {
    setBusy('retire'); setErr('');
    try { await send(`${API}/api/ai-command/use-cases/${ucId}/retire`, 'POST', {}); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div className={`rounded-2xl border p-4 ${retired ? 'border-slate-200 bg-slate-50' : started ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-wide text-slate-400 uppercase">Retirement</div>
          {retired ? (
            <div className="text-sm text-slate-700 mt-1">Retired {fmtDate(uc.retired_at)}. The record stays in the AI inventory.</div>
          ) : started ? (
            <div className="text-sm text-slate-700 mt-1">Requested {fmtDate(uc.retirement_requested_at)}{uc.retirement_reason ? `: ${uc.retirement_reason}` : ''}</div>
          ) : (
            <div className="text-sm text-slate-500 mt-1">When the tool is no longer needed, or no longer complies with the policy, the Business Owner tells compliance here.</div>
          )}
        </div>
        {!started && !asking && canWork && <button onClick={() => setAsking(true)} className="text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50">Request retirement</button>}
      </div>
      {asking && (
        <div className="mt-3 space-y-2">
          <textarea className={INPUT} rows={2} placeholder="Why the tool is no longer needed, or no longer complies" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="flex gap-2">
            <button onClick={request} disabled={busy === 'request' || !reason.trim()} className={`${BTN} bg-slate-900 text-white`}>Send to compliance</button>
            <button onClick={() => setAsking(false)} className={`${BTN} border border-slate-200 text-slate-600`}>Cancel</button>
          </div>
        </div>
      )}
      {started && (
        <ul className="mt-3 space-y-2">
          {lines.map((l) => (
            <li key={l.key} className="flex items-start gap-2.5 text-sm">
              {canWork && !retired ? (
                <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-emerald-600 cursor-pointer" checked={l.done} disabled={busy === l.key} onChange={(e) => toggle(l, e.target.checked)} />
              ) : <span className={l.done ? 'text-emerald-500' : 'text-slate-300'}>{l.done ? '✓' : '○'}</span>}
              <span>
                <span className={l.done ? 'text-slate-500 line-through' : 'text-slate-800'}>{l.label}</span>
                {l.done && <span className="text-xs text-slate-400 ml-2">{l.by_label} · {fmtDate(l.at)}</span>}
              </span>
            </li>
          ))}
          <li className="flex items-start gap-2.5 text-sm"><span className="text-emerald-500">✓</span><span className="text-slate-500">The record is retained in the AI inventory. Nothing here is deleted.</span></li>
        </ul>
      )}
      {started && !retired && compliance && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button onClick={retire} disabled={!complete || busy === 'retire'} className={`${BTN} bg-slate-900 text-white`}>{busy === 'retire' ? 'Retiring…' : 'Retire the tool'}</button>
          {!complete && <span className="text-xs text-slate-500">Every line above is done before the record is retired.</span>}
        </div>
      )}
      {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
    </div>
  );
}
